import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useMemo, useState, useEffect } from 'react'
import {
  type Control,
  type FieldErrors,
  type UseFormWatch,
  type UseFormSetValue,
} from 'react-hook-form'

import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { type SelectOption } from '@/components/ui/MultiSelectAutocomplete'
import { LOCATIONS_QUERY_KEYS } from '@/hooks/use-locations'
import {
  type CreateUserFormData,
  type UpdateUserFormData,
  type UserFormInput,
} from '@/lib/validation/user-schemas'
import { type Location } from '@/models/domain/location'
import { type System, locationService } from '@/services/location-service'

import { LocationGroupField } from './LocationGroupField'
import { LocationsField } from './LocationsField'
import { RetailAccessGuidelines } from './RetailAccessGuidelines'
import { RetailAccessLoadingSkeleton } from './RetailAccessLoadingSkeleton'
import { getLocationPlaceholder, getMultiSelectPlaceholder } from './retailAccessUtils'
import { SystemsField } from './SystemsField'

interface RetailLocationsAccessSectionProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  errors: FieldErrors<UserFormInput>
  watch: UseFormWatch<UserFormInput>
  setValue: UseFormSetValue<UserFormInput>
  systems?: System[]
  isLoadingData?: boolean
}

interface RetailAccessFormFieldsProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  watch: UseFormWatch<UserFormInput>
  errors: FieldErrors<UserFormInput>
  isRootOperator: boolean
  isSysAdmin: boolean
  hasMultipleSystems: boolean
  selectedSystem: string | null
  systemOptions: SelectOption[]
  dbaOptions: SelectOption[]
  districtOptions: SelectOption[]
  locations: Location[]
  isLoadingLocationData: boolean
}

function usePlaceholders(props: RetailAccessFormFieldsProps) {
  const { isRootOperator, isSysAdmin, hasMultipleSystems, selectedSystem, isLoadingLocationData } =
    props
  const shared = { isRootOperator, isSysAdmin, hasMultipleSystems, selectedSystem }

  return {
    dba: getMultiSelectPlaceholder({
      ...shared,
      isAll: false,
      isLoading: isLoadingLocationData,
      entityName: 'DBAs',
    }),
    district: getMultiSelectPlaceholder({
      ...shared,
      isAll: false,
      isLoading: isLoadingLocationData,
      entityName: 'districts',
    }),
    location: getLocationPlaceholder({ ...shared, locationsAll: false }),
  }
}

function RetailAccessFormFields(props: RetailAccessFormFieldsProps) {
  const placeholders = usePlaceholders(props)

  return (
    <div className="space-y-6">
      <SystemsField
        control={props.control}
        watch={props.watch}
        errors={props.errors}
        isRootOperator={props.isRootOperator}
        systemOptions={props.systemOptions}
      />
      <LocationsField
        control={props.control}
        watch={props.watch}
        errors={props.errors}
        locations={props.locations}
        isRootOperator={props.isRootOperator}
        isSysAdmin={props.isSysAdmin}
        hasMultipleSystems={props.hasMultipleSystems}
        selectedSystem={props.selectedSystem}
        isLoadingLocationData={props.isLoadingLocationData}
        placeholder={placeholders.location}
      />
      <LocationGroupField
        control={props.control}
        errors={props.errors}
        fieldName="dbas"
        label="DBAs"
        icon="building"
        options={props.dbaOptions}
        isRootOperator={props.isRootOperator}
        isSysAdmin={props.isSysAdmin}
        hasMultipleSystems={props.hasMultipleSystems}
        selectedSystem={props.selectedSystem}
        isLoadingLocationData={props.isLoadingLocationData}
        placeholder={placeholders.dba}
      />
      <LocationGroupField
        control={props.control}
        errors={props.errors}
        fieldName="districts"
        label="Districts"
        icon="map"
        options={props.districtOptions}
        isRootOperator={props.isRootOperator}
        isSysAdmin={props.isSysAdmin}
        hasMultipleSystems={props.hasMultipleSystems}
        selectedSystem={props.selectedSystem}
        isLoadingLocationData={props.isLoadingLocationData}
        placeholder={placeholders.district}
      />
    </div>
  )
}

export function RetailLocationsAccessSection({
  control,
  errors,
  watch,
  setValue,
  systems = [],
  isLoadingData = false,
}: RetailLocationsAccessSectionProps) {
  const selectedSystems = watch('attributes.systems')
  const [showMultiSystemModal, setShowMultiSystemModal] = useState(false)
  const [previousSystemCount, setPreviousSystemCount] = useState(0)

  const systemsArray = Array.isArray(selectedSystems) ? selectedSystems : []
  const selectedSystem = systemsArray.length === 1 ? systemsArray[0] : null
  const hasMultipleSystems = systemsArray.length > 1

  const realmRoles = watch('realmRoles')
  const isRootOperator = realmRoles.cs_sys_op === true
  const isSysAdmin = realmRoles.cs_sys_admin === true

  useClearOnRoleChange({ isRootOperator, isSysAdmin, setValue })
  useMultiSystemDetection({
    systemsArray,
    previousSystemCount,
    setPreviousSystemCount,
    setShowMultiSystemModal,
  })

  const { dbas, districts, locations, isLoadingLocationData } = useLocationQueries(selectedSystem)
  const systemOptions = useSystemOptions(systems)
  const dbaOptions = useEntityOptions(dbas, selectedSystem)
  const districtOptions = useEntityOptions(districts, selectedSystem)

  if (isLoadingData) return <RetailAccessLoadingSkeleton />

  return (
    <>
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${isRootOperator ? 'opacity-50' : ''}`}
      >
        <div className="p-6 space-y-6">
          <SectionHeader />
          <RoleInfoBanner isRootOperator={isRootOperator} isSysAdmin={isSysAdmin} />
          <RetailAccessFormFields
            control={control}
            watch={watch}
            errors={errors}
            isRootOperator={isRootOperator}
            isSysAdmin={isSysAdmin}
            hasMultipleSystems={hasMultipleSystems}
            selectedSystem={selectedSystem}
            systemOptions={systemOptions}
            dbaOptions={dbaOptions}
            districtOptions={districtOptions}
            locations={locations ?? []}
            isLoadingLocationData={isLoadingLocationData}
          />
          <RetailAccessGuidelines />
        </div>
      </div>

      <ConfirmModal
        isOpen={showMultiSystemModal}
        onClose={() => {
          setShowMultiSystemModal(false)
        }}
        onConfirm={() => {
          setShowMultiSystemModal(false)
        }}
        title="Multiple Systems Selected"
        message="When multiple systems are selected, the user will have access to ALL DBAs, Districts, and Locations for each selected system. Fine-grained access control is only available when a single system is selected. Do you want to continue?"
        confirmText="I Understand"
        cancelText=""
        confirmVariant="primary"
      />
    </>
  )
}

const SectionHeader = () => (
  <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
      Retail Locations-Based Access Control
    </h3>
    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
      Define granular access to specific retail locations and resources
    </p>
  </div>
)

const RoleInfoBanner = ({
  isRootOperator,
  isSysAdmin,
}: {
  isRootOperator: boolean
  isSysAdmin: boolean
}) => {
  if (!isRootOperator && !isSysAdmin) return null

  const message = isRootOperator
    ? 'Root Operator has access to all systems and all data in all systems'
    : 'System Administrator has access to all data within selected system(s)'

  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800 dark:text-blue-200">{message}</p>
      </div>
    </div>
  )
}

function useClearOnRoleChange({
  isRootOperator,
  isSysAdmin,
  setValue,
}: {
  isRootOperator: boolean
  isSysAdmin: boolean
  setValue: UseFormSetValue<UserFormInput>
}) {
  useEffect(() => {
    if (isRootOperator) {
      setValue('attributes.systems', [])
      setValue('attributes.systems_all', false)
      setValue('attributes.dbas', [])
      setValue('attributes.districts', [])
      setValue('attributes.locations', [])
      setValue('attributes.locations_all', false)
      return
    }

    if (isSysAdmin) {
      setValue('attributes.dbas', [])
      setValue('attributes.districts', [])
      setValue('attributes.locations', [])
      setValue('attributes.locations_all', false)
    }
  }, [isRootOperator, isSysAdmin, setValue])
}

function useMultiSystemDetection({
  systemsArray,
  previousSystemCount,
  setPreviousSystemCount,
  setShowMultiSystemModal,
}: {
  systemsArray: string[]
  previousSystemCount: number
  setPreviousSystemCount: (count: number) => void
  setShowMultiSystemModal: (show: boolean) => void
}) {
  useEffect(() => {
    const currentCount = systemsArray.length
    if (previousSystemCount === 1 && currentCount > 1) {
      setShowMultiSystemModal(true)
    }
    setPreviousSystemCount(currentCount)
  }, [systemsArray.length, previousSystemCount, setPreviousSystemCount, setShowMultiSystemModal])
}

function useLocationQueries(selectedSystem: string | null) {
  const { data: dbas, isLoading: isLoadingDBAs } = useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.dbas(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) throw new Error('No system selected')
      return locationService.getDBAs(selectedSystem)
    },
    enabled: !!selectedSystem,
  })

  const { data: districts, isLoading: isLoadingDistricts } = useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.districts(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) throw new Error('No system selected')
      return locationService.getDistricts(selectedSystem)
    },
    enabled: !!selectedSystem,
  })

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.locations(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) throw new Error('No system selected')
      return locationService.getLocations(selectedSystem)
    },
    enabled: !!selectedSystem,
  })

  return {
    dbas,
    districts,
    locations,
    isLoadingLocationData: isLoadingDBAs || isLoadingDistricts || isLoadingLocations,
  }
}

function useSystemOptions(systems: System[]): SelectOption[] {
  return useMemo(
    () =>
      systems
        .filter((s) => s.active)
        .map((s) => ({ id: s.name, name: s.display_name || s.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [systems]
  )
}

function useEntityOptions(
  entities: { id: string; name: string }[] | undefined,
  selectedSystem: string | null
): SelectOption[] {
  return useMemo(() => {
    if (!selectedSystem || !entities) return []
    return entities
      .map((d) => ({ id: d.id, name: d.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [entities, selectedSystem])
}
